import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
    Drawer,
    Typography,
    TextField,
    Grid,
    IconButton,
    InputAdornment,
    Card,
    CardContent,
    Box,
    Tooltip
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { Close as CloseIcon } from "components/MaterialSymbol/MaterialIcons";
import { Search as SearchIcon } from "components/MaterialSymbol/MaterialIcons";
import { Clear as ClearIcon } from "components/MaterialSymbol/MaterialIcons";
import { Add as AddIcon } from "components/MaterialSymbol/MaterialIcons";
import { Check as CheckIcon } from "components/MaterialSymbol/MaterialIcons";

import useCategoryLibrary from "hooks/useCategoryLibrary";

const useStyles = makeStyles((theme) => ({
    drawerPaper: {
        width: 400,
        padding: theme.spacing(2),
        backgroundColor: '#fff'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2)
    },
    searchContainer: {
        marginBottom: theme.spacing(1)
    },
    infoBar: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    gridContainer: {
        height: 'calc(100vh - 180px)',
        overflowY: 'auto',
        paddingRight: theme.spacing(0.5)
    },
    categoryCard: {
        marginBottom: theme.spacing(1),
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: '#fbfbfb',
        }
    },
    cardContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px !important',
    },
    categoryName: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '#333',
        textTransform: 'capitalize',
    },
    addButton: {
        padding: 4,
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light + '20'
        }
    },
    addedIcon: {
        color: theme.palette.success.main,
        fontSize: '1.25rem'
    }
}));

const toTitleCase = (str) => {
    if (!str) return "";
    return str.toLowerCase().split(' ').map(word => {
        return (word.charAt(0).toUpperCase() + word.slice(1));
    }).join(' ');
};

export default function CategoryLibraryDrawer({ open, onClose, onAdd, existingIds = [] }) {
    const classes = useStyles();
    const { categories, isLoading } = useCategoryLibrary();
    const [searchTerm, setSearchTerm] = useState("");

    const filteredCategories = categories.filter(cat => {
        const name = cat.locales?.en?.name || cat.locales?.es?.name || cat.locales?.pt?.name || cat.locales?.fr?.name || "";
        return name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const renderSkeletons = () => (
        <Grid container>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <Grid item xs={12} key={i}>
                    <Box 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="space-between" 
                        mb={1} 
                        px={1.5} 
                        style={{ height: '44px' }}
                    >
                        <Skeleton 
                            variant="rect" 
                            width="75%" 
                            height={20} 
                            style={{ borderRadius: '4px' }} 
                        />
                        <Skeleton 
                            variant="circle" 
                            width={24} 
                            height={24} 
                        />
                    </Box>
                </Grid>
            ))}
        </Grid>
    );

    return (
        <Drawer 
            anchor="right" 
            open={open} 
            onClose={onClose}
            variant="temporary"
            ModalProps={{
                hideBackdrop: true,
                disableEnforceFocus: true,
                disableAutoFocus: true,
                style: { pointerEvents: 'none' }
            }}
            PaperProps={{
                className: classes.drawerPaper,
                style: { pointerEvents: 'auto' },
                elevation: 3
            }}
        >
            <div className={classes.header}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>Category Library</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>

            <div className={classes.searchContainer}>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Search library..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm ? (
                            <InputAdornment position="end">
                                <IconButton 
                                    size="small" 
                                    onClick={() => setSearchTerm("")}
                                    style={{ padding: 2 }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
            </div>

            <div className={classes.infoBar}>
                {isLoading ? (
                    <Typography variant="caption" color="textSecondary">
                        Loading categories...
                    </Typography>
                ) : (
                    <Typography variant="caption" color="textSecondary">
                        {filteredCategories.length} categories available
                    </Typography>
                )}
            </div>

            <div className={classes.gridContainer}>
                {isLoading ? (
                    renderSkeletons()
                ) : (
                    <>
                        {filteredCategories.length > 0 ? (
                            <Grid container>
                                {filteredCategories.map(cat => {
                                    const isAdded = existingIds.includes(cat.id);
                                    
                                    return (
                                        <Grid item xs={12} key={cat.id}>
                                            <Card className={classes.categoryCard} variant="outlined">
                                                <CardContent className={classes.cardContent}>
                                                    <Typography className={classes.categoryName}>
                                                        {toTitleCase(
                                                            cat.locales?.en?.name ||
                                                            cat.locales?.es?.name ||
                                                            cat.locales?.pt?.name ||
                                                            cat.locales?.fr?.name ||
                                                            ""
                                                        )}
                                                    </Typography>
                                                    {isAdded ? (
                                                        <IconButton size="small" disabled>
                                                            <CheckIcon className={classes.addedIcon} />
                                                        </IconButton>
                                                    ) : (
                                                        <Tooltip title="Add category">
                                                            <IconButton 
                                                                className={classes.addButton}
                                                                onClick={() => onAdd(cat)}
                                                                size="small"
                                                            >
                                                                <AddIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        ) : (
                            searchTerm && (
                                <Box textAlign="center" py={4}>
                                    <Typography variant="body2" color="textSecondary">
                                        No results found for "{searchTerm}"
                                    </Typography>
                                </Box>
                            )
                        )}
                    </>
                )}
            </div>
        </Drawer>
    );
}
